import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Megaphone, Undo2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  fetchLiveAnnouncements,
  fetchDismissedIds,
  dismissAnnouncement,
  undismissAnnouncement,
  type Announcement,
} from "@/lib/announcements";
import { useEffect, useState } from "react";

const LOCAL_KEY = "landoo_dismissed_announcements";

function readLocal(): string[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(window.localStorage.getItem(LOCAL_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
}

function writeLocal(ids: string[]) {
  try {
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

export function AnnouncementBanner() {
  const { user, roles, loading } = useAuth();
  const qc = useQueryClient();
  const [localDismissed, setLocalDismissed] = useState<string[]>([]);
  /** Dismissals made during this session, offered for undo until the page reloads. */
  const [undoable, setUndoable] = useState<Announcement[]>([]);

  useEffect(() => setLocalDismissed(readLocal()), []);

  const { data: announcements = [] } = useQuery({
    queryKey: ["announcements", "live", roles.join(",")],
    queryFn: () => fetchLiveAnnouncements(roles),
    enabled: !loading,
    staleTime: 60_000,
  });

  const { data: dismissed = [] } = useQuery({
    queryKey: ["announcements", "dismissed", user?.id],
    queryFn: () => fetchDismissedIds(user!.id),
    enabled: !!user?.id,
  });

  const hidden = new Set([...dismissed, ...localDismissed]);
  const visible: Announcement[] = announcements.filter((a) => !hidden.has(a.id));
  if (visible.length === 0 && undoable.length === 0) return null;

  const dismiss = async (a: Announcement) => {
    const next = [...localDismissed, a.id];
    setLocalDismissed(next);
    writeLocal(next);
    setUndoable((prev) => [...prev.filter((p) => p.id !== a.id), a]);
    if (user?.id) {
      try {
        await dismissAnnouncement(a.id, user.id);
        qc.invalidateQueries({ queryKey: ["announcements", "dismissed", user.id] });
      } catch {
        /* ignore */
      }
    }
  };

  const undo = async (a: Announcement) => {
    const next = localDismissed.filter((id) => id !== a.id);
    setLocalDismissed(next);
    writeLocal(next);
    setUndoable((prev) => prev.filter((p) => p.id !== a.id));
    if (user?.id) {
      try {
        await undismissAnnouncement(a.id, user.id);
        qc.invalidateQueries({ queryKey: ["announcements", "dismissed", user.id] });
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex flex-col items-center gap-2 px-2 pt-2 sm:px-4">
      {visible.map((a) => (
        <div
          key={a.id}
          className="pointer-events-auto w-full max-w-3xl rounded-xl border border-border bg-card/95 p-3 shadow-lg backdrop-blur"
        >
          <div className="flex items-start gap-3">
            <span className="mt-0.5 rounded-lg bg-primary/10 p-1.5 text-primary">
              <Megaphone className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground">{a.title}</p>
              <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{a.message}</p>
            </div>
            <button
              type="button"
              aria-label="Dismiss announcement"
              onClick={() => dismiss(a)}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}

      {undoable.map((a) => (
        <div
          key={`undo-${a.id}`}
          className="pointer-events-auto flex w-full max-w-3xl items-center justify-between gap-3 rounded-xl border border-border bg-muted/95 px-3 py-2 shadow-md backdrop-blur"
        >
          <p className="min-w-0 truncate text-[11px] text-muted-foreground">
            Dismissed “{a.title}”
          </p>
          <button
            type="button"
            onClick={() => undo(a)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-[11px] font-semibold text-foreground transition-colors hover:bg-accent"
          >
            <Undo2 className="h-3 w-3" /> Undo dismiss
          </button>
        </div>
      ))}
    </div>
  );
}
